import React from "react";

const Pagination = ({
  page,
  total,
  perPage,
  onPageChange,
  disabled,
  "aria-label": ariaLabel = "Pagination",
}) => {
  const safeTotal = Math.min(total ?? 0, 1000);
  const totalPages = Math.max(1, Math.ceil(safeTotal / perPage) || 1);
  const current = Math.min(Math.max(1, page), totalPages);
  const start = safeTotal === 0 ? 0 : (current - 1) * perPage + 1;
  const end = Math.min(current * perPage, safeTotal);

  const go = (p) => {
    if (disabled || p < 1 || p > totalPages || p === current) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    onPageChange(p);
  };

  const windowSize = 5;
  let from = Math.max(1, current - Math.floor(windowSize / 2));
  let to = Math.min(totalPages, from + windowSize - 1);
  if (to - from < windowSize - 1) {
    from = Math.max(1, to - windowSize + 1);
  }
  const pages = [];
  for (let i = from; i <= to; i += 1) pages.push(i);

  if (safeTotal === 0 && !disabled) {
    return null;
  }

  return (
    <nav className="gf-pagination" aria-label={ariaLabel}>
      <button
        type="button"
        className="gf-pagination__btn"
        onClick={() => go(current - 1)}
        disabled={disabled || current <= 1}
      >
        ← Prev
      </button>
      <div className="gf-pagination__pages" role="list">
        {from > 1 && (
          <>
            <button
              type="button"
              className={`gf-pagination__num ${current === 1 ? "is-active" : ""}`}
              onClick={() => go(1)}
            >
              1
            </button>
            {from > 2 && <span className="gf-pagination__ellipsis">…</span>}
          </>
        )}
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            role="listitem"
            className={`gf-pagination__num ${p === current ? "is-active" : ""}`}
            onClick={() => go(p)}
            aria-current={p === current ? "page" : undefined}
          >
            {p}
          </button>
        ))}
        {to < totalPages && (
          <>
            {to < totalPages - 1 && (
              <span className="gf-pagination__ellipsis">…</span>
            )}
            <button
              type="button"
              className={`gf-pagination__num ${current === totalPages ? "is-active" : ""}`}
              onClick={() => go(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
      </div>
      <button
        type="button"
        className="gf-pagination__btn"
        onClick={() => go(current + 1)}
        disabled={disabled || current >= totalPages}
      >
        Next →
      </button>
      <p className="gf-pagination__range" aria-live="polite">
        Showing {start}–{end} of {safeTotal.toLocaleString()}
      </p>
    </nav>
  );
};

export default Pagination;
